/**
 * Defining one field.
 *
 * The two decisions here that are not obvious:
 *
 * **The key is set once and never again.** It is what the value is stored under, including
 * inside sealed snapshots, so renaming it would orphan every value already recorded. The
 * label is what changes. On an existing draft the key is shown and disabled, with the reason
 * given rather than left to be discovered.
 *
 * **Collisions are refused as the person types.** The API refuses them too — it has to,
 * since it is the boundary — but finding out on save that the name you chose is already a
 * column is a worse experience than being told while you are choosing it.
 *
 * **A published field opens the same form with most of it locked.** Only what a sealed
 * period does not depend on stays editable — label, help, section, grid slot, workbook
 * mapping — and saving proposes rather than applies. The alternative, a separate cut-down
 * "rename" dialog, would make the two paths drift; here the disabled controls are themselves
 * the explanation of what cannot change and why.
 */
import { useMutation } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api, ApiError } from '@/api/client'
import { Icon } from '@/components/Icon'
import type { BuilderField } from '@/features/builder/FormBuilder'

/** What a person can choose. Mirrors the API's `FieldKind`, which is the authority. */
const KINDS = [
  'text',
  'longtext',
  'integer',
  'number',
  'money',
  'percent',
  'date',
  'datetime',
  'boolean',
  'select',
  'multi_select',
] as const

/** Kinds whose value comes from a fixed list the tenant types. */
const HAS_OPTIONS = new Set<string>(['select', 'multi_select'])

export function FieldEditor({
  slug,
  field,
  reserved,
  taken,
  onClose,
  onSaved,
}: {
  slug: string
  field: BuilderField | null
  reserved: string[]
  taken: string[]
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const first = useRef<HTMLInputElement>(null)

  const [key, setKey] = useState(field?.key ?? '')
  const [labelEn, setLabelEn] = useState(field?.label_en ?? '')
  const [labelAr, setLabelAr] = useState(field?.label_ar ?? '')
  const [kind, setKind] = useState(field?.kind ?? 'text')
  const [required, setRequired] = useState(field?.required ?? false)
  const [inGrid, setInGrid] = useState(field?.in_grid ?? false)
  const [section, setSection] = useState(field?.section ?? '')
  const [workbook, setWorkbook] = useState(field?.workbook_column ?? '')
  const [options, setOptions] = useState(
    ((field?.config?.['options'] as string[] | undefined) ?? []).join('\n'),
  )
  const [backfill, setBackfill] = useState(field?.backfill ?? 'none')
  const [error, setError] = useState<string | null>(null)

  // Editing a live field. Everything that decides how a recorded value is stored or read is
  // locked, and saving proposes a change rather than making one.
  const live = field?.status === 'published'

  useEffect(() => {
    first.current?.focus()
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', escape)
    return () => document.removeEventListener('keydown', escape)
  }, [onClose])

  const optionList = options
    .split('\n')
    .map((option) => option.trim())
    .filter(Boolean)

  // Told while choosing, not on save.
  const collision =
    !field && key
      ? reserved.includes(key)
        ? t('builder.keyIsColumn')
        : taken.includes(key)
          ? t('builder.keyTaken')
          : !/^[a-z][a-z0-9_]{0,62}$/.test(key)
            ? t('builder.keyShape')
            : null
      : null

  const incomplete =
    !labelEn.trim() ||
    (!field && !key) ||
    Boolean(collision) ||
    (HAS_OPTIONS.has(kind) && optionList.length === 0)

  const save = useMutation({
    mutationFn: async () => {
      const config: Record<string, unknown> = {}
      if (HAS_OPTIONS.has(kind)) config['options'] = optionList

      const presentation = {
        label_en: labelEn.trim(),
        label_ar: labelAr.trim() || null,
        required,
        in_grid: inGrid,
        section: section.trim() || null,
        workbook_column: workbook.trim() || null,
      }

      // `config` and `backfill` are absent for a live field on purpose: the API refuses
      // them there, and sending an unchanged value would still be sending them.
      if (live) return api.patch(`/registers/${slug}/fields/${field.id}`, presentation)

      const body = { ...presentation, backfill, config }
      if (field) return api.patch(`/registers/${slug}/fields/${field.id}`, body)
      return api.post(`/registers/${slug}/fields`, { ...body, key, kind })
    },
    onSuccess: onSaved,
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.friendly : String(caught)),
  })

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div
        className="modal modal--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="field-editor-title"
        data-testid="field-editor"
      >
        <header className="modal__head modal__head--plain">
          <Icon name="registers" size={16} />
          <h2 className="modal__title" id="field-editor-title">
            {live
              ? t('builder.renameField')
              : field
                ? t('builder.editField')
                : t('builder.newField')}
          </h2>
        </header>

        <div className="modal__body" data-scroll="1">
          {live ? (
            <p className="modal__note" data-testid="field-live-note">
              {t('builder.liveNote', { version: field.introduced_in_version ?? 0 })}
            </p>
          ) : null}

          <div className="edit__fields">
            <label className="field">
              <span className="field__label">{t('builder.label')}</span>
              <input
                ref={first}
                className="field__input"
                value={labelEn}
                onChange={(event) => setLabelEn(event.target.value)}
                data-testid="field-label"
              />
            </label>

            <label className="field">
              <span className="field__label">{t('builder.labelAr')}</span>
              <input
                className="field__input"
                value={labelAr}
                onChange={(event) => setLabelAr(event.target.value)}
                dir="rtl"
                lang="ar"
                data-testid="field-label-ar"
              />
            </label>

            <label className="field">
              <span className="field__label">{t('builder.key')}</span>
              <input
                className="field__input"
                value={key}
                // Set once. It is what the value is stored under, including inside sealed
                // snapshots, so renaming it would orphan every value already recorded.
                disabled={Boolean(field)}
                onChange={(event) => setKey(event.target.value.toLowerCase())}
                data-testid="field-key"
              />
              <span className="field__hint">
                {field ? t('builder.keyFixed') : (collision ?? t('builder.keyHint'))}
              </span>
            </label>

            <label className="field">
              <span className="field__label">{t('builder.kind')}</span>
              <select
                className="field__input"
                value={kind}
                // The kind decides how the value is stored and validated; changing it on a
                // draft that already has options would leave them meaningless.
                disabled={Boolean(field)}
                onChange={(event) => setKind(event.target.value)}
                data-testid="field-kind"
              >
                {KINDS.map((option) => (
                  <option key={option} value={option}>
                    {t(`builder.kinds.${option}`, option)}
                  </option>
                ))}
              </select>
            </label>

            {HAS_OPTIONS.has(kind) && !live ? (
              <label className="field field--wide">
                <span className="field__label">{t('builder.options')}</span>
                <textarea
                  className="field__input field__input--area"
                  rows={4}
                  value={options}
                  onChange={(event) => setOptions(event.target.value)}
                  placeholder={t('builder.optionsHint')}
                  data-testid="field-options"
                />
                <span className="field__hint">{t('builder.optionsHint')}</span>
              </label>
            ) : null}

            <label className="field">
              <span className="field__label">{t('builder.section')}</span>
              <input
                className="field__input"
                value={section}
                onChange={(event) => setSection(event.target.value)}
                data-testid="field-section"
              />
            </label>

            <label className="field">
              <span className="field__label">{t('builder.workbook')}</span>
              <input
                className="field__input"
                value={workbook}
                onChange={(event) => setWorkbook(event.target.value)}
                data-testid="field-workbook"
              />
              <span className="field__hint">{t('builder.workbookHint')}</span>
            </label>
          </div>

          <div className="builder__toggles">
            <label className="toggle">
              <input
                type="checkbox"
                checked={required}
                onChange={(event) => setRequired(event.target.checked)}
                data-testid="field-required"
              />
              <span>
                {t('builder.required')}
                <span className="toggle__hint">{t('builder.requiredHint')}</span>
              </span>
            </label>

            <label className="toggle">
              <input
                type="checkbox"
                checked={inGrid}
                onChange={(event) => setInGrid(event.target.checked)}
                data-testid="field-in-grid"
              />
              <span>
                {t('builder.inGrid')}
                <span className="toggle__hint">{t('builder.inGridHint')}</span>
              </span>
            </label>

            <label className="toggle" hidden={live}>
              <input
                type="checkbox"
                disabled={live}
                checked={backfill === 'closed_periods'}
                onChange={(event) =>
                  setBackfill(event.target.checked ? 'closed_periods' : 'none')
                }
                data-testid="field-backfill"
              />
              <span>
                {t('builder.backfill')}
                <span className="toggle__hint">{t('builder.backfillHint')}</span>
              </span>
            </label>
          </div>

          {error ? (
            <p className="modal__error" role="alert" data-testid="field-error">
              {error}
            </p>
          ) : null}
        </div>

        <footer className="modal__foot">
          <button type="button" className="button button--quiet" onClick={onClose}>
            {t('edit.cancel')}
          </button>
          <button
            type="button"
            className="button button--primary button--wide"
            disabled={incomplete || save.isPending}
            onClick={() => save.mutate()}
            data-testid="field-save"
          >
            {save.isPending
              ? t('builder.saving')
              : live
                ? t('builder.propose')
                : t('builder.saveDraft')}
          </button>
        </footer>
      </div>
    </>
  )
}
